import { World, CameraPreset } from '../scene/World';
import { EXPERIENCES, Experience } from '../data/experiences';
import { sound } from '../audio/SoundEngine';

export class Hud {
  private world: World;
  private container: HTMLElement;

  // UI Elements
  private topBarEl!: HTMLElement;
  private dockEl!: HTMLElement;
  private infoCardEl!: HTMLElement;
  private hoverTooltipEl!: HTMLElement;
  private terminalDrawerEl!: HTMLElement;
  private terminalLogsEl!: HTMLElement;
  private terminalInputEl!: HTMLInputElement;
  private previewModalEl!: HTMLElement;
  private previewIframeEl!: HTMLIFrameElement;
  private fallbackModalEl!: HTMLElement;

  private isTerminalOpen: boolean = false;
  private isPreviewOpen: boolean = false;
  private isScanlinesOn: boolean = true;

  constructor(world: World) {
    this.world = world;
    this.container = document.createElement('div');
    this.container.className = 'cyber-hud';
    document.body.appendChild(this.container);

    this.buildTopBar();
    this.buildDock();
    this.buildInfoCard();
    this.buildHoverTooltip();
    this.buildTerminalDrawer();
    this.buildPreviewModal();
    this.buildFallbackModal();

    this.bindEvents();
    this.updateExperience(world.currentExperience);
  }

  private buildTopBar() {
    this.topBarEl = document.createElement('header');
    this.topBarEl.className = 'hud-top-bar';
    this.topBarEl.innerHTML = `
      <div class="hud-brand">
        <span class="hud-badge-neon">TOILLE // 3D ARCADE</span>
        <span class="hud-sub">CYBERPUNK WORLD WORKSTATION</span>
      </div>

      <div class="hud-cam-controls">
        <button class="hud-btn active" data-cam="desk" title="Focus Computer Screen">[1] DESK FOCUS</button>
        <button class="hud-btn" data-cam="panoramic" title="Panoramic Room View">[2] ROOM VIEW</button>
        <button class="hud-btn" data-cam="window" title="Metropolis Window View">[3] WINDOW</button>
        <button class="hud-btn" data-cam="autotour" title="Cinematic Auto Tour">[4] AUTO TOUR</button>
      </div>

      <div class="hud-sys-status">
        <button class="hud-btn-icon" id="btn-toggle-sound" title="Toggle Synthesized Audio">
          <span class="icon-label">🔊 AUDIO: ON</span>
        </button>
        <button class="hud-btn-icon" id="btn-toggle-scanlines" title="Toggle CRT Scanlines">
          <span class="icon-label">📺 SCANLINES</span>
        </button>
        <button class="hud-btn-icon" id="btn-toggle-terminal" title="Toggle Cyber Terminal [T]">
          <span class="icon-label">>_ TERMINAL</span>
        </button>
        <button class="hud-btn-icon" id="btn-toggle-2d" title="Open 2D Card List View">
          <span class="icon-label">📋 2D VIEW</span>
        </button>
      </div>
    `;
    this.container.appendChild(this.topBarEl);
  }

  private buildDock() {
    this.dockEl = document.createElement('div');
    this.dockEl.className = 'hud-bottom-dock';
    this.dockEl.innerHTML = `
      <div class="dock-chips">
        <button class="dock-chip active" data-exp="racer">
          <span class="chip-num">01</span>
          <span class="chip-title">TOILLE-RACER</span>
          <span class="chip-tag">ARCADE 2088</span>
        </button>
        <button class="dock-chip" data-exp="ide">
          <span class="chip-num">02</span>
          <span class="chip-title">TOILLE-IDE</span>
          <span class="chip-tag">WASM LAB</span>
        </button>
        <button class="dock-chip" data-exp="fps">
          <span class="chip-num">03</span>
          <span class="chip-title">TOILLE-FPS</span>
          <span class="chip-tag">MORPHEUS UT</span>
        </button>
      </div>
    `;
    this.container.appendChild(this.dockEl);
  }

  private buildInfoCard() {
    this.infoCardEl = document.createElement('div');
    this.infoCardEl.className = 'hud-info-card';
    this.container.appendChild(this.infoCardEl);
  }

  private buildHoverTooltip() {
    this.hoverTooltipEl = document.createElement('div');
    this.hoverTooltipEl.className = 'hud-hover-tooltip';
    this.hoverTooltipEl.style.display = 'none';
    this.container.appendChild(this.hoverTooltipEl);
  }

  private buildTerminalDrawer() {
    this.terminalDrawerEl = document.createElement('div');
    this.terminalDrawerEl.className = 'hud-terminal-drawer';
    this.terminalDrawerEl.innerHTML = `
      <div class="term-header">
        <span class="term-title">TOILLE CYBER-TERMINAL CLI v4.9</span>
        <button class="term-close-btn" id="term-close">&times;</button>
      </div>
      <div class="term-body">
        <div class="term-logs" id="term-logs">
          <div class="term-line info">> Welcome to Toille Cyber-Deck Terminal OS.</div>
          <div class="term-line info">> Type "help" for a list of available cyber commands.</div>
          <div class="term-line info">> Type "launch racer" or "launch ide" to execute directly.</div>
        </div>
        <div class="term-input-row">
          <span class="term-prompt">toille@cyber-matrix:~$</span>
          <input type="text" class="term-input" id="term-input" placeholder="Type a command..." autofocus />
        </div>
      </div>
    `;
    this.container.appendChild(this.terminalDrawerEl);

    this.terminalLogsEl = this.terminalDrawerEl.querySelector('#term-logs')!;
    this.terminalInputEl = this.terminalDrawerEl.querySelector('#term-input')!;
  }

  private buildPreviewModal() {
    this.previewModalEl = document.createElement('div');
    this.previewModalEl.className = 'hud-preview-modal';
    this.previewModalEl.innerHTML = `
      <div class="modal-backdrop"></div>
      <div class="modal-dialog">
        <div class="modal-header">
          <div class="modal-title-group">
            <span class="modal-title-badge">IN-SCENE EXPERIENCE VIEWPORT</span>
            <span class="modal-title-text" id="modal-title">TOILLE-RACER</span>
          </div>
          <div class="modal-actions">
            <a href="#" target="_blank" rel="noopener noreferrer" class="hud-btn-link" id="modal-external-link">OPEN IN NEW TAB ↗</a>
            <button class="modal-close-btn" id="modal-close">&times;</button>
          </div>
        </div>
        <div class="modal-viewport-container">
          <iframe class="modal-iframe" id="modal-iframe" src="" allow="gamepad; accelerometer; gyroscope"></iframe>
        </div>
      </div>
    `;
    this.container.appendChild(this.previewModalEl);

    this.previewIframeEl = this.previewModalEl.querySelector('#modal-iframe')!;
  }

  private buildFallbackModal() {
    this.fallbackModalEl = document.createElement('div');
    this.fallbackModalEl.className = 'hud-fallback-modal';
    this.fallbackModalEl.innerHTML = `
      <div class="modal-backdrop" id="fallback-backdrop"></div>
      <div class="fallback-dialog">
        <div class="modal-header">
          <div class="modal-title-group">
            <span class="modal-title-badge">TOILLE ECOSYSTEM DIRECT DIRECTORY</span>
            <span class="modal-title-text">ALL EXPERIENCES & WEBSITES</span>
          </div>
          <button class="modal-close-btn" id="fallback-close">&times;</button>
        </div>
        <div class="fallback-cards">
          ${Object.values(EXPERIENCES)
            .map(
              (exp) => `
            <div class="fallback-card" style="border-left: 4px solid ${exp.themeColor}">
              <div class="fallback-card-top">
                <span class="card-badge" style="color: ${exp.themeColor}; border-color: ${exp.themeColor}">${exp.category}</span>
                <span class="card-ver">${exp.version}</span>
              </div>
              <h3 class="fallback-title">${exp.title}</h3>
              <p class="fallback-tagline">${exp.tagline}</p>
              <p class="fallback-desc">${exp.description}</p>
              <div class="fallback-specs-tags">
                ${exp.specs.techStack.map((tech) => `<span class="tech-tag">${tech}</span>`).join('')}
              </div>
              <div class="fallback-links">
                ${exp.links
                  .map(
                    (lnk) => `
                  <a href="${lnk.url}" target="_blank" rel="noopener noreferrer" class="fallback-link-btn ${lnk.isPrimary ? 'primary' : ''}">
                    ${lnk.icon || '⚡'} ${lnk.label} [${lnk.badge || 'LINK'}]
                  </a>
                `
                  )
                  .join('')}
              </div>
            </div>
          `
            )
            .join('')}
        </div>
      </div>
    `;
    this.container.appendChild(this.fallbackModalEl);
  }

  public updateExperience(exp: Experience) {
    // 1. Update Dock chip active states
    this.dockEl.querySelectorAll('.dock-chip').forEach((btn) => {
      const id = (btn as HTMLElement).dataset.exp;
      btn.classList.toggle('active', id === exp.id);
    });

    // 2. Render Info Card
    this.infoCardEl.innerHTML = `
      <div class="info-card-header" style="border-bottom: 2px solid ${exp.themeColor}">
        <div class="info-card-meta">
          <span class="info-badge" style="color: ${exp.themeColor}; border: 1px solid ${exp.themeColor}">[${exp.category}]</span>
          <span class="info-codename">${exp.codename}</span>
          <span class="info-ver">${exp.version}</span>
        </div>
        <h2 class="info-title" style="color: #ffffff; text-shadow: 0 0 10px ${exp.themeColor}">${exp.title}</h2>
        <div class="info-tagline" style="color: ${exp.accentColor}">${exp.tagline}</div>
      </div>

      <p class="info-desc">${exp.description}</p>

      <div class="info-stats-grid">
        ${exp.stats
          .map(
            (stat) => `
          <div class="stat-cell">
            <span class="stat-label">${stat.label}</span>
            <span class="stat-value" style="color: ${exp.accentColor}">${stat.value}</span>
          </div>
        `
          )
          .join('')}
      </div>

      <div class="info-actions">
        <a href="${exp.primaryUrl}" target="_blank" rel="noopener noreferrer" class="action-launch-btn" id="action-launch">
          ⚡ LAUNCH ${exp.title}
        </a>
        <button class="action-preview-btn" id="action-preview">
          🖥️ PREVIEW IN SCREEN
        </button>
      </div>

      <div class="info-sublinks">
        ${exp.links
          .filter((l) => !l.isPrimary)
          .map(
            (l) => `
          <a href="${l.url}" target="_blank" rel="noopener noreferrer" class="sublink-btn">
            ${l.icon || '↗'} ${l.label} <span class="sublink-badge">${l.badge}</span>
          </a>
        `
          )
          .join('')}
      </div>
    `;

    // Rebind newly created buttons
    this.infoCardEl.querySelector('#action-launch')?.addEventListener('click', () => {
      sound.playLaunchWarp();
    });

    this.infoCardEl.querySelector('#action-preview')?.addEventListener('click', () => {
      this.openPreview(exp);
    });
  }

  private openPreview(exp: Experience) {
    sound.playClick();
    this.previewModalEl.classList.add('open');
    const titleEl = this.previewModalEl.querySelector('#modal-title')!;
    titleEl.textContent = `${exp.title} (${exp.primaryUrl})`;

    const externalLink = this.previewModalEl.querySelector('#modal-external-link') as HTMLAnchorElement;
    externalLink.href = exp.primaryUrl;

    this.previewIframeEl.src = exp.primaryUrl;
    this.isPreviewOpen = true;
  }

  private closePreview() {
    this.previewModalEl.classList.remove('open');
    this.previewIframeEl.src = '';
    this.isPreviewOpen = false;
  }

  public showHoverTooltip(label: string | null) {
    if (!label) {
      this.hoverTooltipEl.style.display = 'none';
      return;
    }
    this.hoverTooltipEl.textContent = label;
    this.hoverTooltipEl.style.display = 'block';
  }

  private bindEvents() {
    // Camera view preset buttons
    this.topBarEl.querySelectorAll('[data-cam]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const cam = (e.currentTarget as HTMLElement).dataset.cam as CameraPreset;
        this.world.setCameraPreset(cam);
        this.topBarEl.querySelectorAll('[data-cam]').forEach((b) => b.classList.remove('active'));
        (e.currentTarget as HTMLElement).classList.add('active');
      });
    });

    // Sound toggle
    const soundBtn = this.topBarEl.querySelector('#btn-toggle-sound')!;
    soundBtn.addEventListener('click', () => {
      const isMuted = sound.toggleMute();
      soundBtn.querySelector('.icon-label')!.textContent = isMuted ? '🔇 AUDIO: MUTED' : '🔊 AUDIO: ON';
      soundBtn.classList.toggle('active', !isMuted);
    });

    // Scanlines toggle
    const scanlinesBtn = this.topBarEl.querySelector('#btn-toggle-scanlines')!;
    scanlinesBtn.addEventListener('click', () => {
      this.isScanlinesOn = !this.isScanlinesOn;
      document.body.classList.toggle('no-scanlines', !this.isScanlinesOn);
      scanlinesBtn.classList.toggle('active', this.isScanlinesOn);
      sound.playClick();
    });

    // Terminal toggle
    const terminalBtn = this.topBarEl.querySelector('#btn-toggle-terminal')!;
    terminalBtn.addEventListener('click', () => {
      this.toggleTerminal();
    });

    this.terminalDrawerEl.querySelector('#term-close')?.addEventListener('click', () => {
      this.toggleTerminal(false);
    });

    // 2D View toggle
    const fallbackBtn = this.topBarEl.querySelector('#btn-toggle-2d')!;
    fallbackBtn.addEventListener('click', () => {
      this.fallbackModalEl.classList.add('open');
      sound.playClick();
    });

    this.fallbackModalEl.querySelector('#fallback-close')?.addEventListener('click', () => {
      this.fallbackModalEl.classList.remove('open');
    });
    this.fallbackModalEl.querySelector('#fallback-backdrop')?.addEventListener('click', () => {
      this.fallbackModalEl.classList.remove('open');
    });

    // Dock experience selection chips
    this.dockEl.querySelectorAll('[data-exp]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const expId = (e.currentTarget as HTMLElement).dataset.exp as 'racer' | 'ide' | 'fps';
        this.world.selectExperience(expId);
      });
    });

    // Preview modal close
    this.previewModalEl.querySelector('#modal-close')?.addEventListener('click', () => {
      this.closePreview();
    });
    this.previewModalEl.querySelector('.modal-backdrop')?.addEventListener('click', () => {
      this.closePreview();
    });

    // Terminal command input
    this.terminalInputEl.addEventListener('keydown', (e) => {
      sound.playKeyType();
      if (e.key === 'Enter') {
        const val = this.terminalInputEl.value.trim();
        if (val) {
          this.executeTerminalCommand(val);
          this.terminalInputEl.value = '';
        }
      }
    });

    // Global keyboard hotkeys
    window.addEventListener('keydown', (e) => {
      // Ignore if typing inside terminal input
      if (document.activeElement === this.terminalInputEl) {
        if (e.key === 'Escape') {
          this.toggleTerminal(false);
        }
        return;
      }

      if (e.key === '1') {
        this.world.selectExperience('racer');
      } else if (e.key === '2') {
        this.world.selectExperience('ide');
      } else if (e.key === '3') {
        this.world.selectExperience('fps');
      } else if (e.key === 't' || e.key === 'T') {
        e.preventDefault();
        this.toggleTerminal();
      } else if (e.key === 'm' || e.key === 'M') {
        const isMuted = sound.toggleMute();
        soundBtn.querySelector('.icon-label')!.textContent = isMuted ? '🔇 AUDIO: MUTED' : '🔊 AUDIO: ON';
      } else if (e.key === 'c' || e.key === 'C') {
        // Cycle camera views
        const presets: CameraPreset[] = ['desk', 'panoramic', 'window', 'autotour'];
        const currentActive = this.topBarEl.querySelector('.hud-cam-controls .hud-btn.active') as HTMLElement;
        const currentCam = (currentActive?.dataset.cam as CameraPreset) || 'desk';
        const nextIdx = (presets.indexOf(currentCam) + 1) % presets.length;
        const nextCam = presets[nextIdx];
        this.world.setCameraPreset(nextCam);
        this.topBarEl.querySelectorAll('[data-cam]').forEach((b) => {
          b.classList.toggle('active', (b as HTMLElement).dataset.cam === nextCam);
        });
      } else if (e.key === ' ' || e.key === 'Enter') {
        if (!this.isPreviewOpen) {
          sound.playLaunchWarp();
          window.open(this.world.currentExperience.primaryUrl, '_blank', 'noopener,noreferrer');
        }
      } else if (e.key === 'Escape') {
        this.closePreview();
        this.fallbackModalEl.classList.remove('open');
        this.toggleTerminal(false);
      }
    });

    // World listeners
    this.world.onExperienceChange = (exp) => {
      this.updateExperience(exp);
    };

    this.world.onHoverChange = (target) => {
      this.showHoverTooltip(target ? target.label : null);
    };

    this.world.onScreenClick = () => {
      this.openPreview(this.world.currentExperience);
    };
  }

  private toggleTerminal(force?: boolean) {
    this.isTerminalOpen = force !== undefined ? force : !this.isTerminalOpen;
    this.terminalDrawerEl.classList.toggle('open', this.isTerminalOpen);
    sound.playClick();
    if (this.isTerminalOpen) {
      setTimeout(() => this.terminalInputEl.focus(), 100);
    }
  }

  private executeTerminalCommand(cmd: string) {
    const raw = cmd.trim();
    const parts = raw.toLowerCase().split(/\s+/);
    const action = parts[0];
    const arg = parts[1];

    this.addTerminalLine(`toille@cyber-matrix:~$ ${raw}`, 'prompt');

    switch (action) {
      case 'help':
        this.addTerminalLine('Available Cyber Commands:');
        this.addTerminalLine('  racer | 1             - Select Toille-Racer experience');
        this.addTerminalLine('  ide | 2               - Select Toille-IDE workspace');
        this.addTerminalLine('  fps | 3               - Select Toille-FPS arena shooter');
        this.addTerminalLine('  launch racer          - Launch racer (https://racer.toille.uk)');
        this.addTerminalLine('  launch ide            - Launch IDE (https://ide.toille.uk)');
        this.addTerminalLine('  launch fps            - Launch FPS (https://fps.toille.uk)');
        this.addTerminalLine('  telemetry             - Launch racer cockpit telemetry (/telemetry)');
        this.addTerminalLine('  gamepad               - Launch racer mobile gamepad (/gamepad)');
        this.addTerminalLine('  camera <desk|room|window|tour> - Switch 3D camera viewpoint');
        this.addTerminalLine('  audio <on|off>        - Toggle synthesized sound effects');
        this.addTerminalLine('  specs                 - Show active experience system specs');
        this.addTerminalLine('  preview               - Open in-scene interactive preview');
        this.addTerminalLine('  clear                 - Clear terminal buffer');
        break;

      case 'racer':
      case '1':
        this.world.selectExperience('racer');
        this.addTerminalLine('Switched active cartridge to TOILLE-RACER', 'success');
        break;

      case 'ide':
      case '2':
        this.world.selectExperience('ide');
        this.addTerminalLine('Switched active cartridge to TOILLE-IDE', 'success');
        break;

      case 'fps':
      case '3':
        this.world.selectExperience('fps');
        this.addTerminalLine('Switched active cartridge to TOILLE-FPS', 'success');
        break;

      case 'launch':
        if (arg === 'racer') {
          sound.playLaunchWarp();
          this.addTerminalLine('Warping to https://racer.toille.uk...', 'success');
          window.open('https://racer.toille.uk', '_blank', 'noopener,noreferrer');
        } else if (arg === 'ide') {
          sound.playLaunchWarp();
          this.addTerminalLine('Warping to https://ide.toille.uk...', 'success');
          window.open('https://ide.toille.uk', '_blank', 'noopener,noreferrer');
        } else if (arg === 'fps') {
          sound.playLaunchWarp();
          this.addTerminalLine('Warping to https://fps.toille.uk...', 'success');
          window.open('https://fps.toille.uk', '_blank', 'noopener,noreferrer');
        } else {
          this.addTerminalLine('Usage: launch <racer | ide | fps>', 'error');
        }
        break;

      case 'telemetry':
        sound.playLaunchWarp();
        this.addTerminalLine('Opening dual-screen telemetry cockpit at https://racer.toille.uk/telemetry', 'success');
        window.open('https://racer.toille.uk/telemetry', '_blank', 'noopener,noreferrer');
        break;

      case 'gamepad':
        sound.playLaunchWarp();
        this.addTerminalLine('Opening smartphone WebRTC gamepad at https://racer.toille.uk/gamepad', 'success');
        window.open('https://racer.toille.uk/gamepad', '_blank', 'noopener,noreferrer');
        break;

      case 'preview':
        this.openPreview(this.world.currentExperience);
        this.addTerminalLine(`Opening in-scene preview for ${this.world.currentExperience.title}`, 'success');
        break;

      case 'camera':
      case 'cam':
        if (arg === 'desk' || arg === 'room' || arg === 'window' || arg === 'tour') {
          const map: Record<string, CameraPreset> = {
            desk: 'desk',
            room: 'panoramic',
            window: 'window',
            tour: 'autotour',
          };
          const cam = map[arg];
          this.world.setCameraPreset(cam);
          this.topBarEl.querySelectorAll('[data-cam]').forEach((b) => {
            b.classList.toggle('active', (b as HTMLElement).dataset.cam === cam);
          });
          this.addTerminalLine(`Camera preset updated: ${cam}`, 'success');
        } else {
          this.addTerminalLine('Usage: camera <desk | room | window | tour>', 'error');
        }
        break;

      case 'audio':
        if (arg === 'on') {
          if (sound.muted) sound.toggleMute();
          this.addTerminalLine('Synthesized audio ENABLED', 'success');
        } else if (arg === 'off') {
          if (!sound.muted) sound.toggleMute();
          this.addTerminalLine('Synthesized audio MUTED', 'info');
        } else {
          this.addTerminalLine('Usage: audio <on | off>', 'error');
        }
        break;

      case 'specs': {
        const exp = this.world.currentExperience;
        this.addTerminalLine(`=== [${exp.title}] SPECIFICATION ===`, 'success');
        this.addTerminalLine(`Codename: ${exp.codename}`);
        this.addTerminalLine(`Runtime:  ${exp.specs.runtime}`);
        this.addTerminalLine(`Engine:   ${exp.specs.engine}`);
        this.addTerminalLine(`Target:   ${exp.primaryUrl}`);
        exp.specs.features.forEach((f) => this.addTerminalLine(`  • ${f}`));
        break;
      }

      case 'clear':
        this.terminalLogsEl.innerHTML = '';
        break;

      default:
        this.addTerminalLine(`Command not recognized: "${raw}". Type "help" for options.`, 'error');
        break;
    }

    this.terminalLogsEl.scrollTop = this.terminalLogsEl.scrollHeight;
  }

  private addTerminalLine(text: string, type: 'info' | 'success' | 'error' | 'prompt' = 'info') {
    const line = document.createElement('div');
    line.className = `term-line ${type}`;
    line.textContent = text;
    this.terminalLogsEl.appendChild(line);
  }
}
